import { StrictMode, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CalendarClock,
  Car,
  Check,
  ChevronDown,
  CircleGauge,
  Clock3,
  Download,
  Edit3,
  FileUp,
  Filter,
  Gauge,
  History,
  LayoutDashboard,
  Plus,
  RotateCcw,
  Save,
  Search,
  Settings2,
  Trash2,
  Wrench
} from "lucide-react";
import "./styles.css";

type Category =
  | "Engine"
  | "Filters"
  | "Brakes"
  | "Fluids"
  | "Tires"
  | "Electrical"
  | "Drivetrain"
  | "Suspension"
  | "Safety"
  | "Comfort";

type Status = "overdue" | "soon" | "ok" | "unknown";
type View = "all" | Status;

type MaintenanceItem = {
  id: string;
  name: string;
  category: Category;
  intervalMiles: number;
  intervalMonths: number;
  lastMileage: number | null;
  lastDate: string;
  estimatedCost: number;
  notes: string;
  priority: "Normal" | "High";
};

type ServiceRecord = {
  id: string;
  itemName: string;
  itemId: string;
  date: string;
  mileage: number;
  cost: number;
  notes: string;
};

type Vehicle = {
  name: string;
  currentMileage: number;
  dailyMiles: number;
};

type AppData = {
  vehicle: Vehicle;
  items: MaintenanceItem[];
  history: ServiceRecord[];
  settings: {
    soonMiles: number;
    soonDays: number;
  };
};

type ItemWithSchedule = MaintenanceItem & {
  nextMileage: number | null;
  nextDate: string;
  milesLeft: number | null;
  daysLeft: number | null;
  status: Status;
  dueReason: string;
};

const STORAGE_KEY = "car-maintenance-tracker:v1";
const todayIso = () => new Date().toISOString().slice(0, 10);

const categories: Category[] = [
  "Engine",
  "Filters",
  "Brakes",
  "Fluids",
  "Tires",
  "Electrical",
  "Drivetrain",
  "Suspension",
  "Safety",
  "Comfort"
];

const categoryTone: Record<Category, string> = {
  Engine: "tone-engine",
  Filters: "tone-filters",
  Brakes: "tone-brakes",
  Fluids: "tone-fluids",
  Tires: "tone-tires",
  Electrical: "tone-electrical",
  Drivetrain: "tone-drivetrain",
  Suspension: "tone-suspension",
  Safety: "tone-safety",
  Comfort: "tone-comfort"
};

const templates: MaintenanceItem[] = [
  item("engine-oil", "Engine oil", "Engine", 5000, 6, 0, "Synthetic oil interval varies by vehicle."),
  item("oil-filter", "Oil filter", "Filters", 5000, 6, 0, "Replace with each oil change."),
  item("engine-air-filter", "Engine air filter", "Filters", 15000, 12, 0, "Shorten interval in dusty driving."),
  item("cabin-air-filter", "Cabin air filter", "Filters", 12000, 12, 0, "Replace earlier if airflow drops."),
  item("spark-plugs", "Spark plugs", "Engine", 60000, 48, 0, "Iridium plugs often last longer."),
  item("battery", "Battery", "Electrical", 40000, 36, 0, "Load-test before hot or cold seasons."),
  item("serpentine-belt", "Serpentine belt", "Engine", 60000, 48, 0, "Inspect for cracks and glazing."),
  item("timing-belt", "Timing belt", "Engine", 90000, 72, 0, "Critical interval for interference engines.", "High"),
  item("brake-pads", "Brake pads", "Brakes", 30000, 24, 0, "Front pads often wear first.", "High"),
  item("brake-rotors", "Brake rotors", "Brakes", 60000, 48, 0, "Inspect thickness and vibration."),
  item("brake-fluid", "Brake fluid", "Fluids", 30000, 24, 0, "Moisture contamination matters.", "High"),
  item("coolant", "Coolant", "Fluids", 50000, 36, 0, "Use correct specification."),
  item("transmission-fluid", "Transmission fluid", "Drivetrain", 60000, 48, 0, "Check sealed-unit service guidance."),
  item("differential-fluid", "Differential fluid", "Drivetrain", 50000, 48, 0, "Applies to AWD/RWD vehicles."),
  item("transfer-case-fluid", "Transfer case fluid", "Drivetrain", 50000, 48, 0, "Applies to 4WD/AWD vehicles."),
  item("power-steering-fluid", "Power steering fluid", "Fluids", 50000, 48, 0, "Skip if electric steering."),
  item("tire-rotation", "Tire rotation", "Tires", 6000, 6, 0, "Keep wear even."),
  item("wheel-alignment", "Wheel alignment", "Tires", 12000, 12, 0, "Check after tire replacement or curb hits."),
  item("tire-replacement", "Tire replacement", "Tires", 45000, 48, 0, "Track tread depth separately if needed.", "High"),
  item("wiper-blades", "Wiper blades", "Safety", 12000, 12, 0, "Replace when streaking starts."),
  item("headlights", "Headlights and exterior bulbs", "Safety", 24000, 24, 0, "Inspect lenses and bulbs."),
  item("ac-service", "A/C service", "Comfort", 24000, 24, 0, "Check cooling and cabin odor."),
  item("fuel-filter", "Fuel filter", "Filters", 40000, 36, 0, "Some vehicles use lifetime in-tank filters."),
  item("suspension-inspection", "Suspension inspection", "Suspension", 24000, 24, 0, "Inspect shocks, bushings, and ball joints.")
];

const defaultData: AppData = {
  vehicle: {
    name: "My car",
    currentMileage: 42000,
    dailyMiles: 32
  },
  items: templates.map((part, index) => ({
    ...part,
    lastMileage: Math.max(0, 42000 - part.intervalMiles + 500 + index * 120),
    lastDate: shiftDate(-Math.max(25, part.intervalMonths * 23 - index * 2)),
    estimatedCost: estimateCost(part.name)
  })),
  history: [],
  settings: {
    soonMiles: 1000,
    soonDays: 30
  }
};

function item(
  id: string,
  name: string,
  category: Category,
  intervalMiles: number,
  intervalMonths: number,
  estimatedCost: number,
  notes: string,
  priority: "Normal" | "High" = "Normal"
): MaintenanceItem {
  return {
    id,
    name,
    category,
    intervalMiles,
    intervalMonths,
    lastMileage: null,
    lastDate: "",
    estimatedCost,
    notes,
    priority
  };
}

function estimateCost(name: string) {
  const lower = name.toLowerCase();
  if (lower.includes("tire replacement")) return 760;
  if (lower.includes("timing")) return 950;
  if (lower.includes("transmission")) return 260;
  if (lower.includes("brake pad")) return 220;
  if (lower.includes("rotor")) return 360;
  if (lower.includes("battery")) return 180;
  if (lower.includes("spark")) return 180;
  if (lower.includes("coolant")) return 140;
  if (lower.includes("oil")) return 75;
  if (lower.includes("filter")) return 45;
  if (lower.includes("rotation")) return 35;
  if (lower.includes("alignment")) return 110;
  return 95;
}

function shiftDate(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function addMonths(iso: string, months: number) {
  if (!iso || !months) return "";
  const date = new Date(`${iso}T00:00:00`);
  date.setMonth(date.getMonth() + months);
  return date.toISOString().slice(0, 10);
}

function daysBetween(startIso: string, endIso: string) {
  if (!startIso || !endIso) return null;
  const start = new Date(`${startIso}T00:00:00`).getTime();
  const end = new Date(`${endIso}T00:00:00`).getTime();
  return Math.ceil((end - start) / 86400000);
}

function uid(prefix = "id") {
  return `${prefix}-${crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`}`;
}

function formatMiles(value: number | null) {
  if (value === null || Number.isNaN(value)) return "Not set";
  return `${Math.round(value).toLocaleString()} mi`;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value || 0);
}

function formatDate(value: string) {
  if (!value) return "Not set";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(`${value}T00:00:00`));
}

function readData(): AppData {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return defaultData;

  try {
    const parsed = JSON.parse(raw) as Partial<AppData>;
    return {
      vehicle: { ...defaultData.vehicle, ...parsed.vehicle },
      items: Array.isArray(parsed.items) ? parsed.items : defaultData.items,
      history: Array.isArray(parsed.history) ? parsed.history : [],
      settings: { ...defaultData.settings, ...parsed.settings }
    };
  } catch {
    return defaultData;
  }
}

function scheduleItem(item: MaintenanceItem, vehicle: Vehicle, settings: AppData["settings"]): ItemWithSchedule {
  const nextMileage =
    item.lastMileage !== null && item.intervalMiles > 0 ? item.lastMileage + item.intervalMiles : null;
  const nextDate = item.lastDate && item.intervalMonths > 0 ? addMonths(item.lastDate, item.intervalMonths) : "";
  const milesLeft = nextMileage === null ? null : nextMileage - vehicle.currentMileage;
  const daysLeft = nextDate ? daysBetween(todayIso(), nextDate) : null;

  let status: Status = "unknown";
  let dueReason = "Need last service";

  const mileageOverdue = milesLeft !== null && milesLeft <= 0;
  const dateOverdue = daysLeft !== null && daysLeft <= 0;
  const mileageSoon = milesLeft !== null && milesLeft <= settings.soonMiles;
  const dateSoon = daysLeft !== null && daysLeft <= settings.soonDays;

  if (mileageOverdue || dateOverdue) {
    status = "overdue";
    dueReason = mileageOverdue
      ? `${Math.abs(Math.round(milesLeft ?? 0)).toLocaleString()} mi overdue`
      : `${Math.abs(Math.round(daysLeft ?? 0)).toLocaleString()} days overdue`;
  } else if (mileageSoon || dateSoon) {
    status = "soon";
    dueReason = mileageSoon
      ? `${Math.max(0, Math.round(milesLeft ?? 0)).toLocaleString()} mi left`
      : `${Math.max(0, Math.round(daysLeft ?? 0)).toLocaleString()} days left`;
  } else if (milesLeft !== null || daysLeft !== null) {
    status = "ok";
    const mileText = milesLeft !== null ? `${Math.round(milesLeft).toLocaleString()} mi` : "";
    const dayText = daysLeft !== null ? `${Math.round(daysLeft).toLocaleString()} days` : "";
    dueReason = [mileText, dayText].filter(Boolean).join(" / ");
  }

  return {
    ...item,
    nextMileage,
    nextDate,
    milesLeft,
    daysLeft,
    status,
    dueReason
  };
}

function App() {
  const [data, setData] = useState<AppData>(() => readData());
  const [view, setView] = useState<View>("all");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<Category | "All">("All");
  const [editing, setEditing] = useState<MaintenanceItem | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<"tracker" | "history" | "settings">("tracker");
  const importRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  const scheduled = useMemo(
    () => data.items.map((part) => scheduleItem(part, data.vehicle, data.settings)),
    [data.items, data.vehicle, data.settings]
  );

  const sorted = useMemo(() => {
    const order: Record<Status, number> = { overdue: 0, soon: 1, unknown: 2, ok: 3 };
    return [...scheduled].sort((a, b) => {
      const statusSort = order[a.status] - order[b.status];
      if (statusSort !== 0) return statusSort;
      const aMiles = a.milesLeft ?? Number.MAX_SAFE_INTEGER;
      const bMiles = b.milesLeft ?? Number.MAX_SAFE_INTEGER;
      return aMiles - bMiles;
    });
  }, [scheduled]);

  const filtered = sorted.filter((part) => {
    const matchesView = view === "all" || part.status === view;
    const matchesCategory = category === "All" || part.category === category;
    const haystack = `${part.name} ${part.category} ${part.notes}`.toLowerCase();
    const matchesSearch = haystack.includes(query.trim().toLowerCase());
    return matchesView && matchesCategory && matchesSearch;
  });

  const counts = scheduled.reduce(
    (acc, part) => {
      acc[part.status] += 1;
      return acc;
    },
    { overdue: 0, soon: 0, ok: 0, unknown: 0 }
  );

  const nextUp = sorted.filter((part) => part.status === "overdue" || part.status === "soon").slice(0, 5);
  const projectedMonthlyMiles = Math.max(0, Math.round(data.vehicle.dailyMiles * 30));
  const dueBudget = scheduled
    .filter((part) => part.status === "overdue" || part.status === "soon")
    .reduce((total, part) => total + part.estimatedCost, 0);

  function updateVehicle(field: keyof Vehicle, value: string) {
    setData((current) => ({
      ...current,
      vehicle: {
        ...current.vehicle,
        [field]: field === "name" ? value : Math.max(0, Number(value) || 0)
      }
    }));
  }

  function updateSettings(field: keyof AppData["settings"], value: string) {
    setData((current) => ({
      ...current,
      settings: {
        ...current.settings,
        [field]: Math.max(0, Number(value) || 0)
      }
    }));
  }

  function openEditor(part?: MaintenanceItem) {
    setEditing(
      part ?? {
        id: uid("part"),
        name: "",
        category: "Engine",
        intervalMiles: 5000,
        intervalMonths: 6,
        lastMileage: data.vehicle.currentMileage,
        lastDate: todayIso(),
        estimatedCost: 0,
        notes: "",
        priority: "Normal"
      }
    );
    setIsEditorOpen(true);
  }

  function saveItem(next: MaintenanceItem) {
    setData((current) => {
      const exists = current.items.some((part) => part.id === next.id);
      return {
        ...current,
        items: exists
          ? current.items.map((part) => (part.id === next.id ? next : part))
          : [...current.items, next]
      };
    });
    setIsEditorOpen(false);
    setEditing(null);
  }

  function removeItem(id: string) {
    setData((current) => ({
      ...current,
      items: current.items.filter((part) => part.id !== id)
    }));
  }

  function markServiced(part: ItemWithSchedule) {
    const record: ServiceRecord = {
      id: uid("service"),
      itemId: part.id,
      itemName: part.name,
      date: todayIso(),
      mileage: data.vehicle.currentMileage,
      cost: part.estimatedCost,
      notes: part.notes
    };

    setData((current) => ({
      ...current,
      items: current.items.map((itemRow) =>
        itemRow.id === part.id
          ? { ...itemRow, lastMileage: current.vehicle.currentMileage, lastDate: todayIso() }
          : itemRow
      ),
      history: [record, ...current.history].slice(0, 200)
    }));
  }

  function exportData() {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `car-maintenance-${todayIso()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function importData(file: File) {
    const text = await file.text();
    const parsed = JSON.parse(text) as AppData;
    if (!parsed.vehicle || !Array.isArray(parsed.items)) {
      throw new Error("Invalid tracker file");
    }
    setData({
      vehicle: { ...defaultData.vehicle, ...parsed.vehicle },
      items: parsed.items,
      history: Array.isArray(parsed.history) ? parsed.history : [],
      settings: { ...defaultData.settings, ...parsed.settings }
    });
  }

  function resetDemo() {
    setData(defaultData);
    setView("all");
    setCategory("All");
    setQuery("");
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <Car size={22} />
          </span>
          <div>
            <p className="eyebrow">Garage Ledger</p>
            <h1>Car Maintenance Tracker</h1>
          </div>
        </div>
        <nav className="panel-tabs" aria-label="Sections">
          <button
            type="button"
            className={activePanel === "tracker" ? "active" : ""}
            onClick={() => setActivePanel("tracker")}
            title="Tracker"
          >
            <LayoutDashboard size={18} />
            <span>Tracker</span>
          </button>
          <button
            type="button"
            className={activePanel === "history" ? "active" : ""}
            onClick={() => setActivePanel("history")}
            title="History"
          >
            <History size={18} />
            <span>History</span>
          </button>
          <button
            type="button"
            className={activePanel === "settings" ? "active" : ""}
            onClick={() => setActivePanel("settings")}
            title="Settings"
          >
            <Settings2 size={18} />
            <span>Settings</span>
          </button>
        </nav>
      </header>

      <section className="vehicle-strip" aria-label="Vehicle">
        <label>
          <span>Vehicle</span>
          <input
            value={data.vehicle.name}
            onChange={(event) => updateVehicle("name", event.target.value)}
            aria-label="Vehicle name"
          />
        </label>
        <label>
          <span>Current mileage</span>
          <input
            inputMode="numeric"
            value={data.vehicle.currentMileage}
            onChange={(event) => updateVehicle("currentMileage", event.target.value)}
            aria-label="Current mileage"
          />
        </label>
        <label>
          <span>Daily miles</span>
          <input
            inputMode="numeric"
            value={data.vehicle.dailyMiles}
            onChange={(event) => updateVehicle("dailyMiles", event.target.value)}
            aria-label="Daily miles"
          />
        </label>
        <div className="strip-stat">
          <Gauge size={18} />
          <span>{projectedMonthlyMiles.toLocaleString()} mi / month</span>
        </div>
      </section>

      {activePanel === "tracker" && (
        <>
          <section className="summary-grid" aria-label="Overview">
            <SummaryCard label="Overdue" value={counts.overdue} icon={<AlertTriangle size={21} />} tone="danger" />
            <SummaryCard label="Due soon" value={counts.soon} icon={<Clock3 size={21} />} tone="warn" />
            <SummaryCard label="On track" value={counts.ok} icon={<Check size={21} />} tone="good" />
            <SummaryCard label="Due budget" value={formatCurrency(dueBudget)} icon={<CalendarClock size={21} />} />
          </section>

          <section className="workbench">
            <aside className="next-panel" aria-label="Next services">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Next up</p>
                  <h2>Service queue</h2>
                </div>
                <button type="button" className="icon-button" onClick={() => openEditor()} title="Add part">
                  <Plus size={18} />
                </button>
              </div>
              <div className="queue-list">
                {nextUp.length ? (
                  nextUp.map((part) => (
                    <button key={part.id} type="button" className="queue-item" onClick={() => openEditor(part)}>
                      <span className={`status-dot ${part.status}`} />
                      <span>
                        <strong>{part.name}</strong>
                        <small>{part.dueReason}</small>
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="empty-state">
                    <Check size={22} />
                    <span>No urgent parts</span>
                  </div>
                )}
              </div>
            </aside>

            <section className="tracker-panel" aria-label="Maintenance items">
              <div className="toolbar">
                <div className="search-box">
                  <Search size={18} />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search parts"
                    aria-label="Search parts"
                  />
                </div>
                <label className="select-wrap">
                  <Filter size={17} />
                  <select
                    value={category}
                    onChange={(event) => setCategory(event.target.value as Category | "All")}
                    aria-label="Filter category"
                  >
                    <option value="All">All systems</option>
                    {categories.map((itemCategory) => (
                      <option key={itemCategory} value={itemCategory}>
                        {itemCategory}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} />
                </label>
              </div>

              <div className="status-tabs" role="tablist" aria-label="Maintenance status">
                {[
                  ["all", "All", scheduled.length],
                  ["overdue", "Overdue", counts.overdue],
                  ["soon", "Soon", counts.soon],
                  ["ok", "Ok", counts.ok],
                  ["unknown", "Unset", counts.unknown]
                ].map(([tab, label, count]) => (
                  <button
                    key={tab}
                    type="button"
                    className={view === tab ? "active" : ""}
                    onClick={() => setView(tab as View)}
                    role="tab"
                    aria-selected={view === tab}
                  >
                    <span>{label}</span>
                    <strong>{count}</strong>
                  </button>
                ))}
              </div>

              <div className="items-grid">
                {filtered.map((part) => (
                  <MaintenanceCard
                    key={part.id}
                    part={part}
                    onEdit={() => openEditor(part)}
                    onService={() => markServiced(part)}
                    onRemove={() => removeItem(part.id)}
                  />
                ))}
              </div>
            </section>
          </section>
        </>
      )}

      {activePanel === "history" && (
        <section className="history-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Service records</p>
              <h2>Maintenance history</h2>
            </div>
            <button type="button" className="secondary-button" onClick={exportData}>
              <Download size={17} />
              <span>Export</span>
            </button>
          </div>
          <div className="history-list">
            {data.history.length ? (
              data.history.map((record) => (
                <article key={record.id} className="history-row">
                  <span className="history-icon">
                    <Wrench size={18} />
                  </span>
                  <div>
                    <strong>{record.itemName}</strong>
                    <small>
                      {formatDate(record.date)} · {formatMiles(record.mileage)} · {formatCurrency(record.cost)}
                    </small>
                  </div>
                  <p>{record.notes}</p>
                </article>
              ))
            ) : (
              <div className="empty-state large">
                <History size={24} />
                <span>No service records yet</span>
              </div>
            )}
          </div>
        </section>
      )}

      {activePanel === "settings" && (
        <section className="settings-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Controls</p>
              <h2>Tracker settings</h2>
            </div>
          </div>
          <div className="settings-grid">
            <label>
              <span>Due-soon miles</span>
              <input
                inputMode="numeric"
                value={data.settings.soonMiles}
                onChange={(event) => updateSettings("soonMiles", event.target.value)}
              />
            </label>
            <label>
              <span>Due-soon days</span>
              <input
                inputMode="numeric"
                value={data.settings.soonDays}
                onChange={(event) => updateSettings("soonDays", event.target.value)}
              />
            </label>
          </div>
          <div className="settings-actions">
            <button type="button" className="secondary-button" onClick={() => importRef.current?.click()}>
              <FileUp size={17} />
              <span>Import</span>
            </button>
            <button type="button" className="secondary-button" onClick={exportData}>
              <Download size={17} />
              <span>Export</span>
            </button>
            <button type="button" className="secondary-button danger-text" onClick={resetDemo}>
              <RotateCcw size={17} />
              <span>Reset</span>
            </button>
            <input
              ref={importRef}
              type="file"
              accept="application/json"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                importData(file).catch(() => alert("That file could not be imported."));
                event.target.value = "";
              }}
            />
          </div>
        </section>
      )}

      {isEditorOpen && editing && (
        <ItemEditor item={editing} onClose={() => setIsEditorOpen(false)} onSave={saveItem} />
      )}
    </main>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  tone = "neutral"
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  tone?: "neutral" | "danger" | "warn" | "good";
}) {
  return (
    <article className={`summary-card ${tone}`}>
      <span>{icon}</span>
      <div>
        <strong>{value}</strong>
        <small>{label}</small>
      </div>
    </article>
  );
}

function MaintenanceCard({
  part,
  onEdit,
  onService,
  onRemove
}: {
  part: ItemWithSchedule;
  onEdit: () => void;
  onService: () => void;
  onRemove: () => void;
}) {
  const statusLabel: Record<Status, string> = {
    overdue: "Overdue",
    soon: "Due soon",
    ok: "On track",
    unknown: "Unset"
  };

  return (
    <article className={`maintenance-card ${part.status}`}>
      <div className="card-topline">
        <span className={`category-pill ${categoryTone[part.category]}`}>{part.category}</span>
        <span className={`status-pill ${part.status}`}>{statusLabel[part.status]}</span>
      </div>
      <div className="card-title-row">
        <div>
          <h3>{part.name}</h3>
          <p>{part.dueReason}</p>
        </div>
        {part.priority === "High" && (
          <span className="priority-chip">
            <AlertTriangle size={14} />
            High
          </span>
        )}
      </div>
      <div className="metric-grid">
        <Metric label="Last" value={`${formatMiles(part.lastMileage)} · ${formatDate(part.lastDate)}`} />
        <Metric label="Next" value={`${formatMiles(part.nextMileage)} · ${formatDate(part.nextDate)}`} />
        <Metric label="Interval" value={`${part.intervalMiles.toLocaleString()} mi · ${part.intervalMonths} mo`} />
        <Metric label="Estimate" value={formatCurrency(part.estimatedCost)} />
      </div>
      {part.notes && <p className="notes">{part.notes}</p>}
      <div className="card-actions">
        <button type="button" className="primary-button" onClick={onService}>
          <Check size={17} />
          <span>Serviced</span>
        </button>
        <button type="button" className="icon-button" onClick={onEdit} title="Edit">
          <Edit3 size={17} />
        </button>
        <button type="button" className="icon-button danger-text" onClick={onRemove} title="Delete">
          <Trash2 size={17} />
        </button>
      </div>
    </article>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ItemEditor({
  item,
  onClose,
  onSave
}: {
  item: MaintenanceItem;
  onClose: () => void;
  onSave: (item: MaintenanceItem) => void;
}) {
  const [draft, setDraft] = useState(item);

  function update<K extends keyof MaintenanceItem>(field: K, value: MaintenanceItem[K]) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        className="editor-modal"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          onSave({
            ...draft,
            name: draft.name.trim() || "Unnamed part",
            intervalMiles: Math.max(0, Number(draft.intervalMiles) || 0),
            intervalMonths: Math.max(0, Number(draft.intervalMonths) || 0),
            lastMileage: draft.lastMileage === null ? null : Math.max(0, Number(draft.lastMileage) || 0),
            estimatedCost: Math.max(0, Number(draft.estimatedCost) || 0)
          });
        }}
      >
        <div className="section-heading">
          <div>
            <p className="eyebrow">Part record</p>
            <h2>{item.name || "New part"}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} title="Close">
            ×
          </button>
        </div>

        <div className="form-grid">
          <label className="span-2">
            <span>Part name</span>
            <input value={draft.name} onChange={(event) => update("name", event.target.value)} autoFocus />
          </label>
          <label>
            <span>Category</span>
            <select value={draft.category} onChange={(event) => update("category", event.target.value as Category)}>
              {categories.map((partCategory) => (
                <option key={partCategory} value={partCategory}>
                  {partCategory}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Priority</span>
            <select
              value={draft.priority}
              onChange={(event) => update("priority", event.target.value as "Normal" | "High")}
            >
              <option value="Normal">Normal</option>
              <option value="High">High</option>
            </select>
          </label>
          <label>
            <span>Interval miles</span>
            <input
              inputMode="numeric"
              value={draft.intervalMiles}
              onChange={(event) => update("intervalMiles", Number(event.target.value))}
            />
          </label>
          <label>
            <span>Interval months</span>
            <input
              inputMode="numeric"
              value={draft.intervalMonths}
              onChange={(event) => update("intervalMonths", Number(event.target.value))}
            />
          </label>
          <label>
            <span>Last mileage</span>
            <input
              inputMode="numeric"
              value={draft.lastMileage ?? ""}
              onChange={(event) =>
                update("lastMileage", event.target.value === "" ? null : Number(event.target.value))
              }
            />
          </label>
          <label>
            <span>Last service date</span>
            <input type="date" value={draft.lastDate} onChange={(event) => update("lastDate", event.target.value)} />
          </label>
          <label>
            <span>Estimated cost</span>
            <input
              inputMode="numeric"
              value={draft.estimatedCost}
              onChange={(event) => update("estimatedCost", Number(event.target.value))}
            />
          </label>
          <label className="span-2">
            <span>Notes</span>
            <textarea value={draft.notes} onChange={(event) => update("notes", event.target.value)} rows={3} />
          </label>
        </div>

        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary-button">
            <Save size={17} />
            <span>Save</span>
          </button>
        </div>
      </form>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
